import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Phone, Check, ArrowRight } from "lucide-react";
import { SERVICES, COMPANY } from "@/data/site";

export default function CTASection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: form.subject ? `${form.subject}${form.company ? ` — ${form.company}` : ""}` : (form.company || undefined),
        message: `${form.message}${form.phone ? `\n\nPhone: ${form.phone}` : ""}`,
      });
      toast.success("Message sent. We'll be in touch within one business day.");
      setForm({ name: "", phone: "", email: "", company: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-8 md:py-12" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="relative rounded-[2.5rem] overflow-hidden noise" style={{
          background:
            "linear-gradient(135deg, hsl(262 90% 45%) 0%, hsl(240 90% 55%) 45%, hsl(220 90% 55%) 100%)",
        }}>
          {/* moving light streaks */}
          <div className="absolute inset-0 opacity-40" style={{
            background: "repeating-linear-gradient(115deg, transparent 0 30px, rgba(255,255,255,0.08) 30px 32px)",
          }} />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(255,255,255,0.35), transparent 60%)" }} />

          <div className="relative grid lg:grid-cols-5 gap-6 p-6 md:p-10">
            {/* Left: pitch */}
            <div className="lg:col-span-2 text-white p-4 md:p-6 flex flex-col justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/70">Get started</div>
                <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl leading-[1.02]">
                  Let&apos;s talk about your workflows
                </h2>
                <p className="mt-5 text-white/85 max-w-md leading-relaxed">
                  Talk to sales or send us a message. We&apos;ll get back to you within one business day.
                </p>

                <div className="mt-10 h-px bg-white/25" />

                <p className="mt-8 text-white/80 max-w-md leading-relaxed">
                  30 minutes to explore your automation opportunities. No pitch, no pressure — just a focused conversation about your workflow challenges.
                </p>

                <ul className="mt-6 space-y-2">
                  {[
                    "Identify high-impact automation opportunities",
                    "Discuss your current tech stack",
                    "Get a ballpark timeline and approach",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-white/90">
                      <Check className="w-4 h-4 text-white mt-0.5 shrink-0" strokeWidth={3} />
                      <span className="text-sm">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/10 backdrop-blur-sm p-4 border border-white/15">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Call us at</div>
                  <div className="mt-1 text-sm font-medium">{COMPANY.phone}</div>
                </div>
                <div className="rounded-2xl bg-white/10 backdrop-blur-sm p-4 border border-white/15">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Email us</div>
                  <div className="mt-1 text-sm font-medium truncate">{COMPANY.email}</div>
                </div>
              </div>
            </div>

            {/* Right: form card */}
            <form
              onSubmit={submit}
              className="lg:col-span-3 bg-card text-foreground rounded-3xl p-6 md:p-10 space-y-4 shadow-2xl"
              data-testid="cta-contact-form"
            >
              <div className="text-center">
                <div className="mx-auto w-11 h-11 rounded-full bg-muted flex items-center justify-center">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <h3 className="mt-3 font-heading font-bold text-2xl">Schedule a discovery call</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                  Tell us about your goals and we&apos;ll tailor our expertise to fit your needs.
                </p>
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  data-testid="cta-form-name" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="cta-form-phone" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Business email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="cta-form-email" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Company name</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="cta-form-company" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Subject</label>
                  <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="cta-form-subject">
                    <option value="">Select</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Message</label>
                <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about what you want to automate…"
                  className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none"
                  data-testid="cta-form-message" />
              </div>

              <p className="text-xs text-muted-foreground">We&apos;ll only use your info to respond to your inquiry.</p>

              <button disabled={loading} type="submit"
                className="group rounded-full pl-6 pr-2 py-2 btn-dark text-sm font-medium inline-flex items-center gap-3 disabled:opacity-50"
                data-testid="cta-form-submit">
                <span>{loading ? "Sending…" : "Send message"}</span>
                <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
